import {fetchEventSource} from '@microsoft/fetch-event-source';
import apiClient from '../../../components/Okta/axiosInterceptor';
import {oktaAuth, generateNewToken, isTokenExpired} from '../../../components/Okta/AuthService';
import type {AgentTrace, ContentBlock, QueryBlock} from './dummySupervisorData';

export interface SupervisorChart {
  type: 'bar' | 'line' | 'pie' | 'area' | string;
  title: string;
  description?: string;
  xAxis?: string;
  yAxis?: string;
  data: Array<{label: string; value: number}>;
}

export interface SupervisorDetailedFinding {
  node_id?: string;
  capability_id?: string;
  summary_md?: string;
  confidence?: number | null;
  latency_ms?: number;
  status?: string;
}

export interface SupervisorAskResponse {
  status?: string;
  session_id?: string;
  user_id?: string;
  final_result?: {
    outcome?: string;
    aborted_reason?: string | null;
    overall_confidence?: number;
    actual_latency_ms?: number;
    coverage_caveats?: string[];
    executive_summary?: {
      response?: string;
      data?: string;
      charts?: SupervisorChart[];
    };
    // Per-node results (one per sub-query/capability run) — real backend
    // data, shown in <AgentOutputsPanel /> as the "queries & agent outputs"
    // section, standing in for the mockup's literal SQL/Cypher (which the
    // real backend doesn't return).
    detailed_findings?: SupervisorDetailedFinding[];
    // Not populated by the real QUO/PnO backend yet — kept for the
    // still-referenced <SupervisorAgentTrace /> replay component and a
    // possible future richer trace UI.
    agentTrace?: AgentTrace;
    contentBlocks?: ContentBlock[];
    queryBlocks?: QueryBlock[];
  };
}

// Fired for every non-terminal SSE event received while streaming (queued,
// step_start/step_done, pipeline_complete, run_*, node_*, plan_written,
// final_result, ...) so callers can surface live progress before the
// terminal `ask_result` event resolves the promise below.
export type SupervisorStreamEventHandler = (eventName: string, data: any) => void;

// Backend for the /raas2 page (planner/orchestrator "ask" endpoint) — QUO
// (Query Understanding Object) service, streaming variant. Auth is two
// headers: the app's own dev Okta session token (`authorization`, forwarded
// for authentication only, not relayed to the downstream Planner-Orchestrator)
// and a manually-supplied prod Okta token (`prod-okta-token`, see the "Set
// Token" button on the page; this one IS forwarded to PnO as auth.okta_token).
// Same CORS problem as SUPERVISOR_MEMORY_URL below, so it gets the same
// dev-proxy treatment.
const SUPERVISOR_ASK_STREAM_URL =
  process.env.NODE_ENV === 'development'
    ? `${window.location.origin}/api/supervisor-ask-stream`
    : 'https://apim-na-ai.dev.mypepsico.com/cgf/raasv2/v2/api/v2/ask/stream';

export const askSupervisor = async (
  query: string,
  sessionId: string,
  userId: string | undefined,
  signal: AbortSignal,
  onEvent?: SupervisorStreamEventHandler,
  // Backs the "Agent" header toggle: nl2sql when enabled, genie otherwise.
  engine: 'genie' | 'nl2sql' = 'genie',
): Promise<SupervisorAskResponse> => {
  const tokenObj: any = await oktaAuth.tokenManager.get('accessToken');
  let devToken = tokenObj?.accessToken;
  if (isTokenExpired(devToken)) {
    devToken = await generateNewToken();
  }
  const prodOktaToken = sessionStorage.getItem('prodOktaToken')?.trim();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${devToken}`,
  };
  if (prodOktaToken) {
    headers['prod-okta-token'] = prodOktaToken;
  }

  return new Promise<SupervisorAskResponse>((resolve, reject) => {
    let settled = false;

    fetchEventSource(SUPERVISOR_ASK_STREAM_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        user_id: userId,
        session_id: sessionId,
        engine,
        runtime_mode: 'rebuild',
      }),
      signal,
      openWhenHidden: true,
      async onopen(response) {
        if (!response.ok) {
          throw new Error(`Supervisor stream request failed: ${response.status}`);
        }
      },
      onmessage(event) {
        if (!event.data) return;
        let payload: any;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        // Terminal event — mirrors the blocking POST /api/v2/ask response
        // contract, so downstream consumers need no shape changes.
        if (event.event === 'ask_result') {
          settled = true;
          resolve(payload as SupervisorAskResponse);
          return;
        }
        onEvent?.(event.event || 'message', payload);
      },
      onclose() {
        if (!settled) {
          reject(new Error('Supervisor stream closed before a result was received'));
        }
      },
      onerror(err) {
        settled = true;
        // Re-throwing stops fetch-event-source's built-in retry loop — this is
        // a single request/response exchange, not a long-lived subscription.
        throw err;
      },
    }).catch((err) => {
      if (!settled) {
        settled = true;
        reject(err);
      }
    });
  });
};

export interface SupervisorMemoryResponse {
  conversation_id?: string | null;
  message_id?: string | null;
  query?: string;
  request_id?: string;
  suggested_questions?: string[];
  additional_text?: string;
  additional_text_memory?: string;
  status?: string;
  session_id?: string;
  reformulated_query?: string;
  memory_hit?: boolean;
  sql_query?: string[];
  trace_id?: string;
  llm_validation?: boolean | null;
}

// Alternate backend for the /raas2 page, used only while the
// memory toggle (next to "Set Token") is switched on. Separate gateway
// (dev APIM) and auth scheme (static apikey/team/project headers) from
// the default `askSupervisor` planner endpoint above.
//
// The APIM host sits behind a WAF that 403s the browser's CORS preflight
// regardless of the APIM-side CORS policy, so calling it directly from the
// browser never works. In dev, route through the same-origin proxy in
// `src/setupProxy.js` instead — same-origin requests don't trigger a
// preflight at all, and webpack-dev-server forwards the call server-side.
// Built as an absolute URL (rather than a bare relative path) so axios
// doesn't instead resolve it against `apiClient`'s own baseURL
// (REACT_APP_API_BASE_URL) — we need it resolved against the current page
// origin so the dev-server proxy above actually intercepts it.
const SUPERVISOR_MEMORY_URL =
  process.env.NODE_ENV === 'development'
    ? `${window.location.origin}/api/supervisor-memory`
    : `${process.env.REACT_APP_DWDGPT_API}/supervisor/memory`;

export const askSupervisorMemory = async (
  prompt: string,
  signal: AbortSignal,
  sessionId?: string | null,
  lastReformulatedQuery?: string | null,
): Promise<SupervisorMemoryResponse> => {
  const payload: Record<string, unknown> = {
    prompt,
    enable_memory: true,
  };
  // Omitted on the first call of a conversation; once the backend returns
  // session_id/reformulated_query, we carry them forward on every
  // subsequent call so it can maintain multi-turn memory.
  if (sessionId) {
    payload.session_id = sessionId;
  }
  if (lastReformulatedQuery) {
    payload.last_reformulated_query = lastReformulatedQuery;
  }
  const headers: Record<string, string> =
    process.env.NODE_ENV === 'development'
      ? {
          team_id: 'f1a66437-1383-4c88-9b20-8064bb50cd19',
          project_id: '5965b548-9fcf-45de-bd56-ba44d4b8601e',
          user_id: 'monika.sharma1@pepsico.com',
          'x-pepgenx-apikey': '0d9041fa-2460-45a5-b3f4-b137232dd5e9',
          x_Supervisor_Token: 'dapi78c0502615176d87529a164ea18ffca7-3',
          x_Supervisor_Base_URL:
            'https://adb-7901759384367063.3.azuredatabricks.net',
          x_Supervisor_Endpoint: 'mas-b2c3ca7c-endpoint',
          x_Execution_Mode: 'supervisor',
        }
      : {};
  const response = await apiClient.post(
    SUPERVISOR_MEMORY_URL,
    payload,
    {
      signal,
      headers,
    },
  );
    console.log("SUPERVISOR_MEMORY_URL",SUPERVISOR_MEMORY_URL)

  return response?.data;
};
